import { cn } from "@/lib/utils";

export const WEATHER = {
  clear_skies: { label: "Clear Skies", emoji: "🟢", color: "#006747", bg: "#E3F0EA" },
  partly_cloudy: { label: "Partly Cloudy", emoji: "🟡", color: "#8a6d00", bg: "#FBF3D0" },
  storm_watch: { label: "Storm Watch", emoji: "🟠", color: "#B45309", bg: "#FDEBD8" },
  tornado_warning: { label: "Tornado Warning", emoji: "🔴", color: "#B91C1C", bg: "#FBE0E0" },
};

export function WeatherBadge({ value, className }) {
  const w = WEATHER[value];
  if (!w) return null; // forecast comes from the search report; not shown unless known
  return (
    <span data-testid={`weather-badge-${value}`}
      className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold", className)}
      style={{ background: w.bg, color: w.color }}>
      <span>{w.emoji}</span> {w.label}
    </span>
  );
}

export function SectionTitle({ children, subtitle, right }) {
  return (
    <div className="section-band rounded-xl px-6 py-5 flex items-center justify-between brand-fade">
      <div>
        <h2 className="text-xl md:text-2xl font-bold tracking-tight">{children}</h2>
        {subtitle && <p className="text-sm text-white/70 mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function Callout({ children, title }) {
  return (
    <div className="callout rounded-b-lg p-5 shadow-sm">
      {title && <div className="text-xs font-bold uppercase tracking-widest text-[#141B4D] mb-1.5">{title}</div>}
      <div className="text-sm text-[#101820] leading-relaxed whitespace-pre-line">{children}</div>
    </div>
  );
}

const TIER_STYLE = {
  0: { label: "Tier 0 · Admin / Route-it", color: "#141B4D", bg: "#E7E9F2" },
  1: { label: "Tier 1 · Status & Logistics", color: "#1D57A5", bg: "#E1ECF7" },
  2: { label: "Tier 2 · Educational / Procedural", color: "#006747", bg: "#E3F0EA" },
  3: { label: "Tier 3 · Legal Judgment / Strategy", color: "#B91C1C", bg: "#FBE0E0" },
};

export function TierBadge({ tier, className }) {
  const t = TIER_STYLE[tier] || TIER_STYLE[2];
  return (
    <span data-testid={`tier-badge-${tier}`}
      className={cn("inline-flex items-center rounded-full px-3 py-1 text-xs font-bold", className)}
      style={{ background: t.bg, color: t.color }}>
      {t.label}
    </span>
  );
}

export function Pill({ children, tone = "cobalt", className, testId }) {
  const tones = {
    navy: "bg-[#141B4D] text-white",
    cobalt: "bg-[#E1ECF7] text-[#1D57A5]",
    yellow: "bg-[#FFD100] text-[#101820]",
    green: "bg-[#E3F0EA] text-[#006747]",
    pale: "bg-[#BDC5DB]/40 text-[#141B4D]",
    red: "bg-[#FBE0E0] text-[#B91C1C]",
  };
  return (
    <span data-testid={testId} className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold", tones[tone], className)}>
      {children}
    </span>
  );
}

export function Avatar({ name, size = 36 }) {
  const initials = (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="rounded-full flex items-center justify-center font-bold text-white shrink-0"
      style={{ width: size, height: size, background: "#1D57A5", fontSize: size * 0.4 }}>
      {initials}
    </div>
  );
}

export function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Math.round((d - new Date()) / 86400000);
}

export function timeAgo(iso) {
  if (!iso) return "";
  const diff = (new Date() - new Date(iso)) / 3600000;
  if (diff < 1) return `${Math.max(1, Math.round(diff * 60))}m ago`;
  if (diff < 24) return `${Math.round(diff)}h ago`;
  return `${Math.round(diff / 24)}d ago`;
}
